import AsyncJS from 'async';
import CLIENT from '../utils/restClient';
import FileSaver from 'file-saver';
import LANGUAGES_CONTROLLER from './languages';
import { toastActions } from '../components/inAppMessages/toastActions';

import Logger, {LOG_LEVELS, ALL_MESSAGES} from '../utils/logger';

export const MODULE_NAME = 'EXCHANGE_RATES_CONTROLLER';
const log = new Logger(MODULE_NAME, LOG_LEVELS.DEBUG);

function namespaced(name) {
    return MODULE_NAME+'.'+name;
}

const UNLOAD = namespaced('UNLOAD');
const LOAD = namespaced('LOAD');
const LOAD_OK = namespaced('LOAD_OK');
const LOAD_FAIL = namespaced('LOAD_FAIL');
const UPDATE = namespaced('UPDATE');
const UPDATE_WITHOUT_LOADING = namespaced('UPDATE_WITHOUT_LOADING');
const UPDATE_PROGRESS = namespaced('UPDATE_PROGRESS');
const UPDATE_OK = namespaced('UPDATE_OK');
const UPDATE_FAIL = namespaced('UPDATE_FAIL');
const CANCEL_IMPORT = namespaced('CANCEL_IMPORT');
const RESET_CANCEL_IMPORT = namespaced('RESET_CANCEL_IMPORT');
const SHOW_EXCHANGE_RATES_MESSAGE = namespaced('SHOW_EXCHANGE_RATES_MESSAGE');
const DOWNLOAD_CSV_EXPORT = namespaced('DOWNLOAD_CSV_EXPORT');
const DOWNLOAD_CSV_EXPORT_OK = namespaced('DOWNLOAD_CSV_EXPORT_OK');
const DOWNLOAD_CSV_EXPORT_FAIL = namespaced('DOWNLOAD_CSV_EXPORT_FAIL');

//Max number of parallel requests 
//Currently API supports MAX 1
const PARALLEL_LIMIT = 2;

export const ACTION = Object.freeze( {
    UNLOAD,
    LOAD,
    LOAD_OK,
    LOAD_FAIL,
    UPDATE,
    UPDATE_WITHOUT_LOADING,
    UPDATE_PROGRESS,
    UPDATE_OK,
    UPDATE_FAIL,
    CANCEL_IMPORT,
    RESET_CANCEL_IMPORT,
    SHOW_EXCHANGE_RATES_MESSAGE,
    DOWNLOAD_CSV_EXPORT,
    DOWNLOAD_CSV_EXPORT_OK,
    DOWNLOAD_CSV_EXPORT_FAIL
});

export const updateSubStateFromAction = (state = {data: null, loading: false, error: null, upload: null}, action={}) => {

    log.debug('updateSubStateFromAction: ', action, state );
    const getNewState = (action, state) => {
        switch (action.type) {   
            case UNLOAD: 
                return Object.assign({}, state, {error: null, data: null});
            case LOAD:
                return  Object.assign({}, state, {loading: true, error: null});
            case LOAD_OK:
                return  Object.assign({}, state, {loading: false, data: action.response, error: null});
            case LOAD_FAIL:
                return  Object.assign({}, state, {loading: false, data: null, error: action.error});
            case UPDATE:
                return  Object.assign({}, state, {loading: true, error: null, progress: action.progress});
            case UPDATE_WITHOUT_LOADING:
                return  Object.assign({}, state, {loading: false, error: null, progress: action.progress});
            case UPDATE_PROGRESS:
                return  Object.assign({}, state, {error: null, progress: action.progress});
            case UPDATE_OK:
                return Object.assign({}, state, {data: action.response, loading: false, error: null});
            case UPDATE_FAIL:
                return Object.assign({}, state, {loading: false, error: action.error});
            case CANCEL_IMPORT:
                return Object.assign({}, state, {loading: true, cancelImport: true});
            case RESET_CANCEL_IMPORT:
                return Object.assign({}, state, {loading: false, cancelImport: false, progress: action.progress});
            case SHOW_EXCHANGE_RATES_MESSAGE:
                return Object.assign({}, state, {exchangeRatesMessage: action.message});
            case DOWNLOAD_CSV_EXPORT:
                return Object.assign({}, state, {loading: true, error: null});
            case DOWNLOAD_CSV_EXPORT_OK:
                return Object.assign({}, state, {loading: false, error: null});
            case DOWNLOAD_CSV_EXPORT_FAIL:
                return Object.assign({}, state, {loading: false, error: action.error});
            default:
              return state;
        }
    };

    const newState = getNewState(action, state);
    log.debug('updateSubStateFromAction:  newState', newState );
    return newState;
};

export const API = {
    
    formatMessage: (languages, messageKey, params = []) => {
        return LANGUAGES_CONTROLLER.format(languages, messageKey, params);
    },
    
    unload: () => {
        log.debug('unload()')
        return (dispatch) => {
            dispatch({type: UNLOAD});
        };
    },
    
    getAll: (params={}, callback=()=>{})  => {
        log.debug('getAll()');
        return (dispatch) => {
            log.debug('getAll().dispatch');
            dispatch({type: LOAD});
            return CLIENT.getExchangeRates(params)
            .then((response) => {
                dispatch({type: LOAD_OK, response: response});
                callback(null, response);
            })
            .catch((error) => {
                dispatch({type: LOAD_FAIL, error: error});
                callback(error, null);
            });
        };
    },

    getAllWithFilters: (params={}, callback=()=>{})  => {
        log.debug('getAllWithFilters()');
        return (dispatch) => {
            log.debug('getAllWithFilters().dispatch');
            dispatch({type: LOAD});
            return CLIENT.getExchangeRatesFiltered(params)
            .then((response) => {
                dispatch({type: LOAD_OK, response: response});
                callback(null, response);
            })
            .catch((error) => {
                dispatch({type: LOAD_FAIL, error: error});
                callback(error, null);
            });
        };
    },

    retrieveOnline: (apiParams, exchangeRatesFilterParams={}, callback=()=>{}) => {
        log.debug('retrieveOnline(): apiParams: ', apiParams);
        return (dispatch, getState) => {
            log.debug('retrieveOnline().dispatch');
            //Clear any existing errors
            log.clearDictionaryError(ALL_MESSAGES.FAILED_TO_RETRIEVE_EXCHANGE_RATES);
            log.clearSuccessMessage(ALL_MESSAGES.EXCHANGE_RATES_IMPORT_SUCCESS);
            apiParams = Object.assign(apiParams, {parallelLimit: PARALLEL_LIMIT});
            log.debug('retrieveOnline(): apiParams (adjusted parallelLimit): ', apiParams);
            //Total number of batch requests to be processed
            const total = Array.isArray(apiParams.countries) ? (Math.ceil(apiParams.countries.length / PARALLEL_LIMIT)) : 0;
            log.debug('retrieveOnline(): total: ', total);
            let progressTracker = {total: total, count: 0, successes: [], failures: []};
            log.debug('retrieveOnline(): progressTracker: ', progressTracker);
            dispatch({type: UPDATE_WITHOUT_LOADING, progress: Object.assign({}, progressTracker)}); 
            //1. Prepare the batch to make parallel requests
            let batchedCountries = [];
            let countries = [...apiParams.countries];
            while(countries.length > 0) {
                batchedCountries.push(countries.splice(0, apiParams.parallelLimit));
            }
            //2. Create a task that makes a request to get all exchange details for batched countries
            let batchedTasks = batchedCountries.map( (groupedCountries) => {
                return (done) => {
                    log.debug('retrieveOnline(): batchedImportTasks.callback: ', getState.cancelImport);
                    const state = getState();
                    if(state && state.exchangeRates && state.exchangeRates.cancelImport) {
                        return done(null, null);
                    } else {
                        const batchApiParams = Object.assign({}, apiParams, {countries: groupedCountries});
                        return CLIENT.exchangeRatesRetrieveOnlineAndStore(batchApiParams)
                        .then((batchResult) => {         
                            log.debug('retrieveOnline(): exchangeRatesRetrieveOnlineAndStore.callback: batchResult: ', batchResult);
                            progressTracker.count = progressTracker.count + 1;
                            log.debug('retrieveOnline(): exchangeRatesRetrieveOnlineAndStore.callback: progress: ', progressTracker);
                            dispatch({type: UPDATE_WITHOUT_LOADING, progress: progressTracker}); 
                            if(!(state && state.exchangeRates && state.exchangeRates.cancelImport)) {
                                done(null, batchResult);
                            }
                        })
                        .catch((error) => {
                            //Here we will only collect the errors received, but continue to fetch the next batch of request
                            log.debug('retrieveOnline(): exchangeRatesRetrieveOnlineAndStore.callback: error: ', error);
                            progressTracker.failures = progressTracker.failures.concat(groupedCountries);
                            log.debug('retrieveOnline(): exchangeRatesRetrieveOnlineAndStore.callback: failed batch: %j', progressTracker.failures);
                            progressTracker.count = progressTracker.count + 1;
                            log.debug('retrieveOnline(): exchangeRatesRetrieveOnlineAndStore.callback: progress: ', progressTracker);                            
                            dispatch({type: UPDATE_WITHOUT_LOADING, progress: progressTracker}); 
                            if(!(state && state.exchangeRates && state.exchangeRates.cancelImport)) {
                                done(error, null);
                            }
                        });
                    }
                };
            });

            //We utilize async js to execute the batch of request sequentially
            AsyncJS.series(
                batchedTasks,
                function(err, results) {
                    //We make a call to get all exchange rates once all the batched tasks to retrieve exchange rates online are finished
                    log.debug('retrieveOnline(): getExchangeRates.callback: err: ', err);
                    log.debug('retrieveOnline(): getExchangeRates.callback: results: ', results);
                    if(err) {
                        dispatch({type: UPDATE_FAIL, error: err});
                    } else {
                        dispatch({type: UPDATE_OK, response: results});
                    }
                    dispatch({type: LOAD});              
                    CLIENT.getExchangeRatesFiltered(exchangeRatesFilterParams)
                        .then((getExchangeRateResponse) => {
                            log.debug('retrieveOnline(): getExchangeRates.callback: getExchangeRateResponse: ', getExchangeRateResponse);
                            //In case of any error, we will display error message to a user
                            if(err) {
                                log.setDictionaryMessage(ALL_MESSAGES.FAILED_TO_RETRIEVE_EXCHANGE_RATES, progressTracker.failures);
                            } else {
                                log.setDictionaryMessage(ALL_MESSAGES.EXCHANGE_RATES_IMPORT_SUCCESS);
                            }      
                            dispatch({type: LOAD_OK, response: getExchangeRateResponse});
                            dispatch({type: RESET_CANCEL_IMPORT, progress: progressTracker});
                            callback(null, getExchangeRateResponse);
                        })
                        .catch((e) => {
                            log.debug('retrieveOnline(): getExchangeRates.callback: error: ', e);
                            log.setDictionaryMessage(ALL_MESSAGES.FAILED_TO_RETRIEVE_EXCHANGE_RATES, progressTracker.failures);                            
                            dispatch({type: LOAD_FAIL, error: e});
                            dispatch({type: RESET_CANCEL_IMPORT, progress: progressTracker});
                            callback(e, null);
                        });
                }
            );
        };
    },
    
    removeAll: (callback=()=>{}) => {
        log.debug('removeAll()');
        return (dispatch) => {
            log.debug('removeAll().dispatch');
            dispatch({type: UPDATE});
            return CLIENT.deleteExchangeRates()
            .then((response) => {
                dispatch({type: UPDATE_OK, response: response});
                callback(null, response);
            })
            .catch((error) => {
                dispatch({type: UPDATE_FAIL, error: error});
                callback(error, null);
            });
        };
    },

    cancelImport: () => {
        log.debug('cancelImport()');
        return (dispatch) => {
            log.debug('cancelImport().dispatch');
            dispatch({type: CANCEL_IMPORT});
        };
    },
    
    importExchangeRateXml :(files=[],removeExistingRates, successHandler, errorHandler) =>{
        log.clearSuccessMessage(ALL_MESSAGES.EXCHANGE_RATES_IMPORT_SUCCESS);
        return (dispatch) => {
            dispatch({type: UPDATE});
            return CLIENT.importExchangeRateXml(files,removeExistingRates)
            .then((response) => {
                log.debug('importExchangeRateXml(): succeeded');
                dispatch({type: UPDATE_OK, response}); 
                successHandler(response);
                
            })
            .catch((error={}) => {
                log.debug('importExchangeRateXml(): error', error );
                dispatch({type: UPDATE_FAIL, error});
                errorHandler(error);
          });
      };
    },

    doDownloadExchangeRatesCsv: (params) => {
        return async (dispatch, getState) => {
            // Helper function to dispatch export toast notifications
            const dispatchExportToast = (titleKey, messageKey, type) => {
                const languages = getState().languages;
                const title = LANGUAGES_CONTROLLER.format(languages, titleKey);
                const message = LANGUAGES_CONTROLLER.format(languages, messageKey);
                dispatch(toastActions.addToast(
                    'exchange-rates-export',
                    title,
                    message,
                    type,
                    'exchangeRates'
                ));
            };

            // Helper function to determine error message key based on error
            const getErrorMessageKey = (error) => {
                if (error.status === 404) return 'exchangeRates_exportNotFound';
                if (error.status === 400) return 'exchangeRates_exportBadRequest';
                if (error.status >= 500) return 'exchangeRates_exportServerError';
                if (error.name === 'NetworkError' || !error.status) return 'exchangeRates_exportNetworkError';
                return 'exchangeRates_exportFailed';
            };

            try {
                dispatch({ type: DOWNLOAD_CSV_EXPORT });
                log.debug('doDownloadExchangeRatesCsv(): params: ', params);

                const file = await CLIENT.downloadExchangeRatesCsv(params);
                const fileName = `ExchangeRates_${params.startDate}_${params.endDate}.csv`;

                try {
                    FileSaver.saveAs(file, fileName);
                } catch (error) {
                    log.debug('doDownloadExchangeRatesCsv(): error saving file to filesystem');
                    dispatchExportToast('exchangeRates_exportFailedTitle', 'exchangeRates_exportSaveFailed', 'error');
                    dispatch({ type: DOWNLOAD_CSV_EXPORT_FAIL, error });
                    return;
                }

                // Success - dispatch success toast
                dispatchExportToast('exchangeRates_exportSuccessTitle', 'exchangeRates_exportSuccess', 'success');
                dispatch({ type: DOWNLOAD_CSV_EXPORT_OK, response: file.blob });
                
            } catch (error) {
                log.debug('doDownloadExchangeRatesCsv(): error', error);
                const errorMessageKey = getErrorMessageKey(error);
                dispatchExportToast('exchangeRates_exportFailedTitle', errorMessageKey, 'error');
                dispatch({ type: DOWNLOAD_CSV_EXPORT_FAIL, error });
            }
        };
    },

    doImportExchangeRates: (operationRequest, successCallback, failureCallback) => {
        log.debug('doImportExchangeRates(): operationRequest: ', operationRequest);
        return async (dispatch) => {
            dispatch({type: UPDATE});
            try {
                const response = await CLIENT.doImportExchangeRates(operationRequest);
                log.debug('doImportExchangeRates(): succeeded');
                dispatch({ type: UPDATE_OK, response });
                if (typeof successCallback === 'function') {
                    successCallback(response);
                }
            } catch (error) {
                log.debug('doImportExchangeRates(): error', error);
                dispatch({ type: UPDATE_FAIL, error });
                if (typeof failureCallback === 'function') {
                    failureCallback(error);
                }
            }
        };
    }
};

export default API;